/* eslint-disable valid-jsdoc */
/**
 * editable.js - a xbrowser library for contentEditable
 *
 * https://github.com/benvinegar/editable.js
 *
 * MIT License
 */
define('core/editable',[],function () {
    'use strict';

    var document = window.document;
    var character = 'character';
    var nbspRe = new RegExp(String.fromCharCode(160), 'gi');
    var blockElemList = 'h1 h2 h3 h4 h5 h6 p pre blockquote address ul ol dir menu li dl div form'.split(' ');
    var blockElems = {};
    var i = 0;

    // build a list of block level elements
    for (i = 0; i < blockElemList.length; i++)
        blockElems[blockElemList[i]] = true;

    function normalizeSpace(str) {
        return str.replace(nbspRe, ' ');
    }

    /**
     * Helper function to recursively aggregate all text from a set
     * of HTML nodes
     */
    function getTextHelper(nodes, nodeCallback, blockJoin) {
        var text = '';
        var blocks = [];
        var name, node, tmp, i;

        if (typeof blockJoin !== 'string')
            blockJoin = '\n\n';

        for (i = 0; i < nodes.length; ++i) {
            node = nodes[i];
            name = node.nodeName.toLowerCase();

            if (node.nodeType === 1) { // html node
                tmp = nodeCallback && nodeCallback(node);

                if (tmp) {
                    text += tmp;
                } else if (blockElems.hasOwnProperty(name)) {
                    // Inside a block-level element, ignore any subsequent block-level
                    // elements (i.e. nested divs or paragraphs)
                    if (text)
                        blocks.push(text);
                    text = getTextHelper(node.childNodes, nodeCallback, blockJoin);
                } else if (name === 'br') {
                    // Always convert breaking tags to newlines
                    text += '\n';
                } else {
                    // Some other (inline) element; recur inside to extract text
                    text += getTextHelper(node.childNodes, nodeCallback, blockJoin);
                }
            }
            else if (node.nodeType === 3) { // text node
                text += normalizeSpace(node.nodeValue);
            }
        }
        // Block elements should get two new lines normally
        blocks.push(text);
        return blocks.join(blockJoin);
    }

    var Editable = function (elem, emulate, overrides) {
        var self = this;

        if (!elem || !elem.contentEditable)
            throw new Error('First argument must be contentEditable');

        this.elem = elem;
        this.emulateTextarea = elem.getAttribute('plaintext-only') || emulate;

        if (this.emulateTextarea) {
            this.pasteHandler = function (evt) {
                var pasted = evt && evt.clipboardData || window.clipboardData;
                // check that the pasted content is text
                // TODO: Support directly pasted images
                if (pasted && !pasted.getData('text')) {
                    evt.preventDefault();
                    evt.stopPropagation();
                }
                var func = self.plainTextReformat;
                var later = function () {
                    func.timeout = null;
                    func.call(self);
                };
                if (func.timeout)
                    clearTimeout(func.timeout);
                func.timeout = setTimeout(later, 0);
            };

            elem.addEventListener('paste', this.pasteHandler);
        }
        for (var prop in overrides) {
            if (overrides.hasOwnProperty(prop))
                this[prop] = overrides[prop];
        }
    };

    Editable.prototype = {

        /**
         * helper function for inserting random html
         */
        insertHTML: function (html) {
            if (document.all) {
                var range = document.selection.createRange();
                range.pasteHTML(html);
                range.collapse(false);
                return range.select();
            }

            return document.execCommand('insertHTML', false, html);
        },

        insertNode: function (node) {
            var selection, range, html;
            if (window.getSelection) {
                selection = window.getSelection();
                if (selection.getRangeAt && selection.rangeCount) {
                    range = selection.getRangeAt(0);
                    range.deleteContents();
                    range.insertNode(node);
                    range.collapse(false);
                    selection.removeAllRanges();
                    selection.addRange(range);
                }
            } else if (document.selection && document.selection.createRange) {
                range = document.selection.createRange();
                html = node.nodeType === 3 ? node.data : node.outerHTML;
                range.pasteHTML(html);
                range.collapse(false);
            }
        },

        /**
         * Grab all text nodes relative to their parents
         */
        // TODO make this function private and have the public version
        // only return textNodes for the wrapped textArea
        getTextNodes: function (nodeList) {
            var elem = this.elem;

            // special case a single node by massaging into a list of nodes
            if (nodeList && nodeList.nodeType)
                nodeList = [nodeList];
            // if nothing is passed in, get text nodes for elem
            else if (!nodeList)
                nodeList = elem.childNodes;

            var textNodes = [];

            for (var i = 0, node; i < nodeList.length; ++i) {
                node = nodeList[i];

                if (!node)
                    continue; // HACK to avoid erroring on whitespace nodes

                switch (node.nodeType) {
                case 1:
                    textNodes = textNodes.concat(this.getTextNodes(node.childNodes));
                    break;
                case 3:
                    // HACK don't count garbage FF nodes
                    if (!/^\n\s+/.test(node.nodeValue))
                        textNodes.push(node);
                    break;
                }
            }
            return textNodes;
        },

        /**
         * Get unformatted text
         */
        text: function (nodeCallback) {
            var elem = this.elem;
            var out, nodes, i;

            // massage the NodeList into an Array of HTMLElements
            try {
                nodes = Array.prototype.slice.call(elem.childNodes);
            } catch (err) {
                nodes = [];
                for (i = 0; i < elem.childNodes.length; ++i)
                    nodes.push(elem.childNodes[i]);
            }
            out = getTextHelper(nodes, nodeCallback, this.emulateTextarea && '\n');
            return out.replace(/^\s+|\s+$/g, ''); // trim the text
        },

        setText: function (str) {
            str = str || '';  // ensure we have a string
            var el = document.createDocumentFragment();
            // 2 consequent line breaks -> we have a paragraph unless we emulate
            var paragraphs = [str]; // we fix the input in TextareaView's fixInputStructure now

            var lenP = paragraphs && paragraphs.length;
            var i, par, paragraph;

            for (i = 0; i < lenP; i++) {
                par = paragraphs[i];
                // parse string and convert it into dom tree wrapped in paragraph
                paragraph = this.createParagraph(par);
                // add paragraph to dom
                el.appendChild(paragraph);
            }
            // We'll keep only the last paragraphs excess <br> since it is
            // necessary for editing.
            el.lastChild.appendChild(document.createElement('br'));

            this.elem.innerHTML = '';  // clear first
            this.elem.appendChild(el);

            // Fix Chrome bug where cursor is sometimes placed before the first element.
            // Selection.modify() causes the page to scroll to the bottom in Firefox
            // and isn't supported in IE.
            if ('WebkitAppearance' in document.documentElement.style && window.navigator.userAgent.indexOf('Firefox') === -1 && window.navigator.userAgent.indexOf('MSIE') === -1) {
                // NOTE: These methods of browser-detection are not future-proof and
                // Selection.modify() is considered a non-standard feature, so we
                // might want to find an alternative solution.
                // https://developer.mozilla.org/en-US/docs/Web/API/Selection/modify
                var sel = window.getSelection && window.getSelection();
                if (sel && sel.anchorNode === this.elem && sel.modify)
                    sel.modify('move', 'forward', 'line');
            }
        },

        createParagraph: function (text) {
            var paragraph = document.createElement('p');
            var i, j, lines, line, lenL, lenC, children;

            // line break inside "paragraph" -> Text + <br>
            lines = text.split(/\r\n|\r|\n/);
            for (j = 0, lenL = lines.length; j < lenL; j++) {
                line = lines[j];
                // Note the line below escapes HTML tags!
                // i.e. <b>Yo</b> becomes &lt;b&gt;Yo&lt;/b&gt;
                children = this.getHtmlElements(line);
                for (i = 0, lenC = children.length; i < lenC; i++)
                    paragraph.appendChild(children[i]);

                paragraph.appendChild(document.createElement('br'));
            }

            // If we do have a last child in the paragraph, it is a line
            // break which we don't want. "ali".split("\n") would give you
            // ["ali"] where we add a <br> at the end of EVERY item.
            if (paragraph.lastChild)
                paragraph.removeChild(paragraph.lastChild);

            return paragraph;
        },

        // Accepts a string and return an array of HTMLElements created from the string
        getHtmlElements: function (line) {
            return [document.createTextNode(line)];
        },

        plainTextReformat: function () {
            if (this.elem.getElementsByTagName('p').length <= 1)
                return;

            this.emulateTextarea = false;
            var rawText = this.text();
            this.emulateTextarea = true;
            this.setText(rawText);
        },

        removeNode: function (node) {
            var prev, sel, range;

            // Webkit/FF
            if (window.getSelection) {
                // HACK in webkit, deleting the node that contains the current
                // range means that there are no more ranges in the Selection
                // object. This means that we have to programatically set the
                // range on the node previous to the mention in order to still
                // maintain document focus
                prev = node.previousSibling;
                node.parentNode.removeChild(node);
                sel = window.getSelection();
                range = document.createRange();
                if (prev) {
                    range.setStart(prev, prev.length);
                    range.setEnd(prev, prev.length);
                }
                sel.addRange(range);
            }
            // IE
            else {
                // TODO port this over to using the selection
                // code, it's complicated so maybe not the best
                // idea to do right now
                node.parentNode.removeChild(node);
            }
        },
        /**
         * Get currently selected text node in
         * the contentEditable element.
         */
        selectedTextNode: function () {
            var elem = this.elem;
            var sel, range, node, textNode, textNodes, prevNode, snippet, i, j;

            // Webkit/Firefox
            if (window.getSelection) {
                sel = window.getSelection();
                return sel.anchorNode;
            }
            // Internet Explorer
            else if (document.selection.createRange) {
                // I wish that you never have to touch this code. You're probably sitting here
                // looking at this function saying "wtf" to yourself becuase it's so hiddeous.
                // If Internet Explorer 9 every becomes the lowest rung on the Microsoft browser family,
                // simple delete this conditional and never look back. If you have the misfortune of
                // modifying the following snippet, I wish you the best of luck in your endeavor.
                range = document.selection.createRange().duplicate();

                // Microsoft thinks about the entire contentEditable container like a single
                // line of text. Because of this, you won't be able to get the anchorNode of
                // the current selection. What we're doing here is simply moving the caret to
                // the front of the entire block of text.
                while (range.moveStart(character, -1000) === -1000)
                    continue;

                var text = range.text;
                // The trick is that we know where the end of our caret is
                // so all we have to do is loop over all text nodes and find
                // out where the two strings differ. Notice how we are truncating
                // the copied string for each iteration of the inner loop. This
                // is done so that when the two strings differ, we can simply
                // compare the remaining piece of the copied string with the
                // current node, this saves us an extra couple of loops.
                for (i = 0; i < elem.childNodes.length; ++i) {

                    node = elem.childNodes[i];
                    textNodes = this.getTextNodes(node);

                    for (j = 0; j < textNodes.length; ++j) {
                        textNode = textNodes[j];
                        snippet = normalizeSpace(textNode.nodeValue);

                        if (text.indexOf(snippet) > -1) {
                            prevNode = textNode;
                            text = text.replace(snippet, '');
                        }
                        // special case where textNode content is longer
                        // than the selected portion of the textNode
                        else if (snippet.indexOf(text) > -1) {
                            return textNode;
                        }
                    }
                }
                return prevNode;
            }
        },

        /**
         * Get relative offset in the currently active text node
         */
        selectedTextNodeOffset: function (node) {
            var range, offset, newOffset;

            // Webkit/Firefox
            if (window.getSelection) {
                var sel = window.getSelection();
                // wondering if this should really
                // be the focus offset, does it even
                // really matter? probably not me thinks
                if (sel && sel.anchorOffset)
                    newOffset = sel.anchorOffset;
            }

            // Internet Explorer
            else if (node && document.selection.createRange) {
                var textNodeText = normalizeSpace(node.nodeValue);

                range = document.selection.createRange();
                var r2 = range.duplicate();
                var prevParent = r2.parentElement();

                // move backwards over the range and compare the selected text
                // node text with the range. break if either we've found a match
                // or the previous range we create each iteration has a different
                // parent element
                for (offset = 0; range.moveStart(character, -1) !== 0; offset++) {
                    if (
                        textNodeText.indexOf(normalizeSpace(range.text)) === 0 ||
                        prevParent !== range.parentElement()
                    ) break;

                    r2 = range.duplicate();
                    prevParent = r2.parentElement();
                }
                newOffset = offset;
            }


            return isNaN(newOffset) ? 0 : newOffset;
        },

        /**
         * Get cursor offset relative to results returned by .text().
         */
        offset: function () {
            var sel = window.getSelection();
            if (!(sel && sel.anchorNode && sel.anchorNode.nodeType === 3))  // This method only supports text nodes
                return 0;

            var elem = this.elem;
            var nodes;

            // massage the NodeList into an Array of HTMLElements
            try {
                nodes = Array.prototype.slice.call(elem.childNodes);
            } catch (error) {
                nodes = [];
                for (var i = 0; i < elem.childNodes.length; ++i)
                    nodes.push(elem.childNodes[i]);
            }

            function getTextAroundCursor(nodes, blockJoin) {
                if (typeof blockJoin !== 'string')
                    blockJoin = '\n\n';

                var sections = [];
                var text = '';

                function extendBySections(nextSections) {
                    text += nextSections[0];
                    for (var i = 1; i < nextSections.length; ++i) {
                        sections.push(text);
                        text = nextSections[i];
                    }
                }

                for (var i = 0; i < nodes.length; ++i) {
                    var node = nodes[i];
                    var name = node.nodeName.toLowerCase();

                    if (node.nodeType === 1) { // html node
                        if (blockElems.hasOwnProperty(name)) {
                            if (text)
                                text += blockJoin;
                            extendBySections(getTextAroundCursor(node.childNodes, blockJoin));
                        } else if (name === 'br') {
                            text += '\n';
                        } else {
                            extendBySections(getTextAroundCursor(node.childNodes, blockJoin));
                        }
                    } else if (node.nodeType === 3) { // text node
                        if (node === sel.anchorNode) {
                            text += normalizeSpace(node.nodeValue.slice(0, sel.anchorOffset));
                            sections.push(text);
                            text = normalizeSpace(node.nodeValue.slice(sel.anchorOffset));
                        } else {
                            text += normalizeSpace(node.nodeValue);
                        }
                    }
                }

                // Block elements should get two new lines normally
                sections.push(text);
                return sections;
            }

            var sections = getTextAroundCursor(nodes, this.emulateTextarea && '\n');
            if (sections.length === 1)  // Cursor was not found
                return 0;

            var offset = sections[0].length;
            var out = sections.join('');

            // Correct for trailing whitespace
            var trailingSpaces = out.match(/\s+$/);
            if (trailingSpaces) {
                var trailingSpaceCount = trailingSpaces[0].length;
                offset = Math.min(offset, out.length - trailingSpaceCount);
            }

            // Correct for leading whitespace
            var leadingSpaces = out.match(/^\s+/);
            if (leadingSpaces) {
                var leadingSpaceCount = leadingSpaces[0].length;
                offset -= leadingSpaceCount;
            }

            return offset;
        },

        /**
         * Select some text in the contentEditable div
         */
        selectNodeText: function (node, start, end) {
            var elem = this.elem;
            var sel, range;

            // Webkit/Firefox
            if (window.getSelection) {
                // clear all ranges
                sel = window.getSelection();
                sel.removeAllRanges();
                // select the new one
                range = document.createRange();
                range.setStart(node, start);
                range.setEnd(node, end);
                sel.addRange(range);
                return sel;
            }

            // Internet Explorer
            else if (document.selection.createRange) {
                range = document.selection.createRange();

                // KLUDGE
                // if there is a substring match before we hit the start of the text node
                // then this code will break. Might want to think about a more robust way
                // about doing this.
                var text = normalizeSpace(node.nodeValue);

                // MASSIVE HACK for ie < 9, clicks change the focus, so we need to
                // focus back on the contentEditable div and refind out
                // start position. The rest of the function can continue as
                // normal afterwards.
                if (range.parentElement().nodeName.toLowerCase() === 'body') {
                    elem.focus();
                    range = document.selection.createRange();
                    // expand over the entire extArea
                    while (range.moveStart(character, -1000) === -1000)
                        continue;
                    while (range.moveEnd(character, 1000) === 1000)
                        continue;
                    var rangeText = normalizeSpace(range.text);
                    var index = rangeText.indexOf(text);
                    if (index > 0)
                        range.moveStart(character, index + 2); // put us inside the range
                    range.collapse();
                }

                // move start cursor to start of text node
                while (range.moveStart(character, -1) === -1 && text.indexOf(normalizeSpace(range.text)) !== 0)
                    continue;

                // move end cursor to end of text node
                while (range.moveEnd(character, 1) === 1 && text !== normalizeSpace(range.text))
                    continue;

                // move the start and end indicies of the Range and select
                range.moveStart(character, start);
                range.moveEnd(character, -1 * (end - start - range.text.length));
                range.select();

                return range;
            }
        },
    };

    Editable.normalizeSpace = normalizeSpace;

    // assign to the current window
    return Editable;
});


